"use client";

import type { ReactNode } from "react";
import Link from "next/link";
import { useEffect, useMemo } from "react";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { X, ShieldCheck, Sparkles } from "lucide-react";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import { cn } from "@/lib/utils";
import CompareCarGallery from "@/components/compare/CompareCarGallery";
import { compareQueryFromIds, writeCompareCarIds } from "@/lib/compare-cars";

export type CompareCarPayload = {
  id: string;
  title: string;
  brand: string;
  model: string;
  year: number;
  price_per_day: number;
  deposit: number | null;
  location: string | null;
  currency: string;
  transmission: string;
  fuel_type: string;
  body_type: string | null;
  seats: number | null;
  engine_volume: string | null;
  is_available: boolean;
  is_listing_verified: boolean;
  out_of_city_allowed: boolean | null;
  out_of_city_surcharge: number | null;
  busyLabel: string | null;
  vendor: { id: string; slug: string | null; company_name: string } | null;
  imageUrls: string[];
  detailPath: string;
  vendorPath: string | null;
};

function fuelLabel(tFuel: ReturnType<typeof useTranslations>, key: string) {
  if (["petrol", "diesel", "electric", "hybrid"].includes(key)) {
    return tFuel(key as "petrol" | "diesel" | "electric" | "hybrid");
  }
  return key;
}

function transmissionLabel(tTrans: ReturnType<typeof useTranslations>, key: string) {
  if (key === "manual" || key === "automatic") {
    return tTrans(key as "manual" | "automatic");
  }
  return key;
}

const BODY_KEYS = [
  "sedan",
  "hatchback",
  "liftback",
  "wagon",
  "coupe",
  "convertible",
  "suv",
  "crossover",
  "minivan",
  "pickup",
  "van",
  "other",
] as const;

function bodyLabel(tBody: ReturnType<typeof useTranslations>, raw: string | null) {
  if (!raw) return null;
  const k = raw.toLowerCase().replace(/\s+/g, "_");
  if (BODY_KEYS.includes(k as (typeof BODY_KEYS)[number])) {
    return tBody(k as "sedan");
  }
  return raw;
}

function CompareTableRowSpec({
  label,
  cars,
  render,
}: {
  label: string;
  cars: CompareCarPayload[];
  render: (c: CompareCarPayload) => ReactNode;
}) {
  return (
    <tr className="border-b border-gray-100 dark:border-gray-800">
      <th
        scope="row"
        className="sticky left-0 z-[2] whitespace-nowrap border-r border-gray-100 bg-gray-50/95 py-3 pl-4 pr-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 backdrop-blur-sm dark:border-gray-800 dark:bg-gray-900/95 dark:text-gray-400"
      >
        {label}
      </th>
      {cars.map((c) => (
        <td key={c.id} className="border-l border-gray-50 bg-white dark:border-gray-900 dark:bg-gray-950">
          {render(c)}
        </td>
      ))}
    </tr>
  );
}

type Props = { cars: CompareCarPayload[] };

export default function CarComparisonPanel({ cars }: Props) {
  const router = useRouter();
  const t = useTranslations("ComparePage");
  const tFuel = useTranslations("Catalog.fuel");
  const tTrans = useTranslations("Catalog.transmission");
  const tBody = useTranslations("Catalog.body");

  const ids = cars.map((c) => c.id);

  useEffect(() => {
    writeCompareCarIds(ids);
  }, [ids]);

  const minPrice = useMemo(
    () => Math.min(...cars.map((c) => Number(c.price_per_day))),
    [cars],
  );

  const minDeposit = useMemo(() => {
    const nums = cars.map((c) => {
      const d = c.deposit;
      if (d == null || Number(d) <= 0) return 0;
      return Number(d);
    });
    return Math.min(...nums);
  }, [cars]);

  function pushCompareUrl(nextIds: string[]) {
    writeCompareCarIds(nextIds);
    if (nextIds.length < 2) {
      router.replace("/cars/compare");
      return;
    }
    router.replace(`/cars/compare?ids=${compareQueryFromIds(nextIds)}`);
  }

  function ColumnHeader({ c }: { c: CompareCarPayload }) {
    return (
      <th className="sticky top-0 z-[3] min-w-[180px] max-w-none border-b border-gray-200 bg-white px-2 py-3 align-top md:min-w-[200px] md:px-3 dark:border-gray-800 dark:bg-gray-950">
        <div className="relative">
          <button
            type="button"
            onClick={() => pushCompareUrl(ids.filter((id) => id !== c.id))}
            className="absolute -right-1 -top-1 z-[10] flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800 dark:hover:text-gray-200"
            aria-label={t("removeFromCompare")}
          >
            <X size={18} strokeWidth={2} aria-hidden />
          </button>
          <div className="relative mb-3 w-full">
            <CompareCarGallery imagePriority imageUrls={c.imageUrls} alt={c.title} sizes="(max-width: 768px) 50vw, min(520px, 28vw)" />
          </div>
          <Link
            href={c.detailPath}
            className="line-clamp-2 text-left text-sm font-semibold text-gray-900 hover:text-[#E63946] dark:text-gray-100"
          >
            {c.brand} {c.model}
          </Link>
          <div className="text-xs text-gray-500 dark:text-gray-400">{c.year}</div>
          {c.busyLabel ? (
            <p className="mt-1 text-[11px] leading-snug text-amber-700 dark:text-amber-400">
              {t("busyUntil", { date: c.busyLabel })}
            </p>
          ) : null}
        </div>
      </th>
    );
  }

  function priceCellContent(c: CompareCarPayload) {
    const p = Number(c.price_per_day);
    const best = p === minPrice && cars.length > 1;
    return (
      <div className="px-3 py-3">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className={cn("font-bold tabular-nums text-gray-900 dark:text-gray-100", best && "text-emerald-700 dark:text-emerald-400")}>
            {Math.round(p)} {c.currency.trim()}
          </span>
          {best ? (
            <span className="inline-flex items-center gap-0.5 rounded-md bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300">
              <Sparkles size={10} aria-hidden />
              {t("bestPrice")}
            </span>
          ) : null}
        </div>
      </div>
    );
  }

  function depositCellContent(c: CompareCarPayload) {
    const val = c.deposit != null && Number(c.deposit) > 0 ? Number(c.deposit) : 0;
    const best = val === minDeposit && cars.length > 1;
    const text =
      c.deposit != null && Number(c.deposit) > 0
        ? `${Math.round(Number(c.deposit))} ${c.currency.trim()}`
        : t("dash");
    return (
      <div className="px-3 py-3">
        <span className={cn("tabular-nums text-gray-800 dark:text-gray-200", best && "font-semibold text-emerald-700 dark:text-emerald-400")}>
          {text}
        </span>
        {best && val > 0 ? (
          <span className="ml-1.5 text-[10px] font-medium text-emerald-700 dark:text-emerald-400">{t("lowestDeposit")}</span>
        ) : null}
      </div>
    );
  }

  function MobileCard({ c }: { c: CompareCarPayload }) {
    const p = Number(c.price_per_day);
    const bestPrice = p === minPrice && cars.length > 1;
    const val = c.deposit != null && Number(c.deposit) > 0 ? Number(c.deposit) : 0;
    const bestDep = val === minDeposit && cars.length > 1;
    const body = bodyLabel(tBody, c.body_type);

    return (
      <article
        className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-950"
        aria-labelledby={`compare-card-${c.id}`}
      >
        <div className="relative border-b border-gray-100 dark:border-gray-800">
          <button
            type="button"
            onClick={() => pushCompareUrl(ids.filter((id) => id !== c.id))}
            className="absolute right-2 top-2 z-[10] flex h-9 w-9 items-center justify-center rounded-xl bg-white/90 text-gray-600 shadow-sm backdrop-blur hover:bg-white dark:bg-gray-900/90 dark:text-gray-300"
            aria-label={t("removeFromCompare")}
          >
            <X size={18} />
          </button>
          <div className="relative w-full">
            <CompareCarGallery imagePriority imageUrls={c.imageUrls} alt={c.title} sizes="(max-width: 640px) 100vw, 90vw" />
          </div>
          <div className="p-4">
            <h2 id={`compare-card-${c.id}`} className="text-lg font-bold text-gray-900 dark:text-gray-100">
              <Link href={c.detailPath} className="hover:text-[#E63946]">
                {c.brand} {c.model}
              </Link>
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">{c.year}</p>
            {c.busyLabel ? (
              <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">{t("busyUntil", { date: c.busyLabel })}</p>
            ) : null}
            <div className="mt-3 flex flex-wrap gap-2">
              <div
                className={cn(
                  "rounded-xl bg-gray-50 px-3 py-2 dark:bg-gray-900",
                  bestPrice && "ring-2 ring-emerald-500/40",
                )}
              >
                <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{t("pricePerDay")}</div>
                <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                  <span className="text-lg font-bold tabular-nums text-gray-900 dark:text-gray-100">
                    {Math.round(p)} {c.currency.trim()}
                  </span>
                  {bestPrice ? (
                    <span className="text-[10px] font-semibold text-emerald-700 dark:text-emerald-400">{t("bestPrice")}</span>
                  ) : null}
                </div>
              </div>
              <div className={cn("rounded-xl bg-gray-50 px-3 py-2 dark:bg-gray-900", bestDep && val > 0 && "ring-2 ring-emerald-500/30")}>
                <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{t("deposit")}</div>
                <div className="mt-0.5 text-base font-medium tabular-nums text-gray-800 dark:text-gray-200">
                  {c.deposit != null && Number(c.deposit) > 0
                    ? `${Math.round(Number(c.deposit))} ${c.currency.trim()}`
                    : t("dash")}
                  {bestDep && val > 0 ? (
                    <span className="ml-1 text-xs font-semibold text-emerald-700 dark:text-emerald-400">{t("lowestDeposit")}</span>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
        <dl className="grid gap-0 divide-y divide-gray-100 text-sm dark:divide-gray-800">
          <div className="flex justify-between gap-3 px-4 py-2.5">
            <dt className="text-gray-500 dark:text-gray-400">{t("verified")}</dt>
            <dd>
              {c.is_listing_verified ? (
                <Badge variant="success" className="gap-1 text-xs">
                  <ShieldCheck size={11} aria-hidden />
                  {t("yes")}
                </Badge>
              ) : (
                t("dash")
              )}
            </dd>
          </div>
          <div className="flex justify-between gap-3 px-4 py-2.5">
            <dt className="text-gray-500 dark:text-gray-400">{t("bodyAndSeats")}</dt>
            <dd className="text-right text-gray-800 dark:text-gray-200">
              {body ?? t("dash")}
              {c.seats != null ? ` · ${t("seatsCount", { count: c.seats })}` : ""}
            </dd>
          </div>
          <div className="flex justify-between gap-3 px-4 py-2.5">
            <dt className="text-gray-500 dark:text-gray-400">{t("engineVolume")}</dt>
            <dd className="text-gray-800 dark:text-gray-200">{c.engine_volume ?? t("dash")}</dd>
          </div>
          <div className="flex justify-between gap-3 px-4 py-2.5">
            <dt className="text-gray-500 dark:text-gray-400">{t("transmissionFuel")}</dt>
            <dd className="text-right capitalize text-gray-800 dark:text-gray-200">
              {transmissionLabel(tTrans, c.transmission)} · {fuelLabel(tFuel, c.fuel_type)}
            </dd>
          </div>
          <div className="flex justify-between gap-3 px-4 py-2.5">
            <dt className="text-gray-500 dark:text-gray-400">{t("catalogStatus")}</dt>
            <dd className="text-gray-800 dark:text-gray-200">{c.is_available ? t("catalogAvailable") : t("catalogUnavailable")}</dd>
          </div>
          <div className="flex justify-between gap-3 px-4 py-2.5">
            <dt className="text-gray-500 dark:text-gray-400">{t("location")}</dt>
            <dd className="max-w-[60%] text-right text-gray-800 dark:text-gray-200">{c.location ?? t("dash")}</dd>
          </div>
          <div className="flex justify-between gap-3 px-4 py-2.5">
            <dt className="text-gray-500 dark:text-gray-400">{t("outOfCity")}</dt>
            <dd className="text-right text-gray-800 dark:text-gray-200">
              {!c.out_of_city_allowed ? (
                t("outOfCityNo")
              ) : c.out_of_city_surcharge != null && Number(c.out_of_city_surcharge) > 0 ? (
                <>
                  {t("outOfCityYes")}{" "}
                  <span className="tabular-nums">
                    (+{Math.round(Number(c.out_of_city_surcharge))} {c.currency.trim()})
                  </span>
                </>
              ) : (
                t("outOfCityYesFree")
              )}
            </dd>
          </div>
          <div className="flex justify-between gap-3 px-4 py-2.5">
            <dt className="text-gray-500 dark:text-gray-400">{t("vendor")}</dt>
            <dd className="max-w-[60%] text-right">
              {c.vendorPath && c.vendor ? (
                <Link href={c.vendorPath} className="font-medium text-[#E63946] hover:underline">
                  {c.vendor.company_name}
                </Link>
              ) : (
                t("dash")
              )}
            </dd>
          </div>
        </dl>
        <div className="flex flex-wrap gap-2 border-t border-gray-100 p-4 dark:border-gray-800">
          <Link href={c.detailPath} className="flex-1 min-w-[8rem]">
            <Button variant="outline" size="sm" className="w-full">
              {t("details")}
            </Button>
          </Link>
          <Link href={`/booking/${c.id}`} className="flex-1 min-w-[8rem]">
            <Button size="sm" className="w-full bg-[#E63946] hover:bg-[#c1121f]">
              {t("booking")}
            </Button>
          </Link>
        </div>
      </article>
    );
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-600 dark:text-gray-400">{t("footnote")}</p>

      {/* Mobile */}
      <div className="flex flex-col gap-5 md:hidden">{cars.map((c) => <MobileCard key={c.id} c={c} />)}</div>

      {/* Desktop */}
      <div className="hidden max-h-[min(72vh,calc(100vh-11rem))] overflow-auto rounded-2xl border border-gray-200 shadow-sm md:block dark:border-gray-800">
        <table className="relative w-full min-w-[780px] border-collapse text-left text-sm">
          <thead>
            <tr>
              <th
                scope="col"
                className="sticky left-0 top-0 z-[4] min-w-[140px] border-b border-r border-gray-200 bg-gray-50 py-3 pl-4 pr-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-400"
              >
                {t("paramColumn")}
              </th>
              {cars.map((c) => (
                <ColumnHeader key={c.id} c={c} />
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-gray-100 dark:border-gray-800">
              <th
                scope="row"
                className="sticky left-0 z-[2] border-r border-gray-100 bg-gray-50/95 py-3 pl-4 pr-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 backdrop-blur-sm dark:border-gray-800 dark:bg-gray-900/95 dark:text-gray-400"
              >
                {t("pricePerDay")}
              </th>
              {cars.map((c) => (
                <td key={c.id} className="border-l border-gray-50 bg-white dark:border-gray-900 dark:bg-gray-950">
                  {priceCellContent(c)}
                </td>
              ))}
            </tr>
            <CompareTableRowSpec cars={cars} label={t("deposit")} render={(c) => depositCellContent(c)} />
            <CompareTableRowSpec
              cars={cars}
              label={t("verified")}
              render={(c) =>
                c.is_listing_verified ? (
                  <div className="px-3 py-3">
                    <Badge variant="success" className="gap-1 text-xs">
                      <ShieldCheck size={11} aria-hidden />
                      {t("yes")}
                    </Badge>
                  </div>
                ) : (
                  <div className="px-3 py-3 text-gray-400">{t("dash")}</div>
                )
              }
            />
            <CompareTableRowSpec
              cars={cars}
              label={t("bodyType")}
              render={(c) => <div className="px-3 py-3">{bodyLabel(tBody, c.body_type) ?? t("dash")}</div>}
            />
            <CompareTableRowSpec
              cars={cars}
              label={t("seatsCountShort")}
              render={(c) => <div className="px-3 py-3">{c.seats != null ? t("seatsCount", { count: c.seats }) : t("dash")}</div>}
            />
            <CompareTableRowSpec
              cars={cars}
              label={t("engineVolume")}
              render={(c) => <div className="px-3 py-3">{c.engine_volume ?? t("dash")}</div>}
            />
            <CompareTableRowSpec
              cars={cars}
              label={t("transmissionFuel")}
              render={(c) => (
                <div className="px-3 py-3 capitalize">
                  {transmissionLabel(tTrans, c.transmission)} · {fuelLabel(tFuel, c.fuel_type)}
                </div>
              )}
            />
            <CompareTableRowSpec
              cars={cars}
              label={t("catalogStatus")}
              render={(c) => (
                <div className="px-3 py-3">{c.is_available ? t("catalogAvailable") : t("catalogUnavailable")}</div>
              )}
            />
            <CompareTableRowSpec cars={cars} label={t("location")} render={(c) => <div className="px-3 py-3">{c.location ?? t("dash")}</div>} />
            <CompareTableRowSpec
              cars={cars}
              label={t("outOfCity")}
              render={(c) => (
                <div className="px-3 py-3">
                  {!c.out_of_city_allowed ? (
                    t("outOfCityNo")
                  ) : c.out_of_city_surcharge != null && Number(c.out_of_city_surcharge) > 0 ? (
                    <>
                      {t("outOfCityYes")}{" "}
                      <span className="tabular-nums">
                        (+{Math.round(Number(c.out_of_city_surcharge))} {c.currency.trim()})
                      </span>
                    </>
                  ) : (
                    t("outOfCityYesFree")
                  )}
                </div>
              )}
            />
            <CompareTableRowSpec
              cars={cars}
              label={t("vendor")}
              render={(c) => (
                <div className="px-3 py-3">
                  {c.vendorPath && c.vendor ? (
                    <Link href={c.vendorPath} className="font-medium text-[#E63946] hover:underline">
                      {c.vendor.company_name}
                    </Link>
                  ) : (
                    t("dash")
                  )}
                </div>
              )}
            />
            <CompareTableRowSpec
              cars={cars}
              label={t("actions")}
              render={(c) => (
                <div className="flex flex-wrap gap-x-3 gap-y-1 px-3 py-3">
                  <Link href={c.detailPath} className="font-semibold text-[#E63946] hover:underline">
                    {t("details")}
                  </Link>
                  <Link href={`/booking/${c.id}`} className="text-gray-600 hover:underline dark:text-gray-400">
                    {t("booking")}
                  </Link>
                </div>
              )}
            />
          </tbody>
        </table>
      </div>
    </div>
  );
}
